import { useEffect, useState } from "react";
import { API, errMsg, fmtDate } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X } from "lucide-react";

const EMPTY_T = { name: "", role: "", content: "", rating: 5, video_url: "", is_visible: true };

export default function Content() {
  const [testimonials, setTestimonials] = useState([]);
  const [messages, setMessages] = useState([]);
  const [form, setForm] = useState(null);
  const [editId, setEditId] = useState(null);

  const load = () => {
    API.get("/testimonials", { params: { all: true } }).then((r) => setTestimonials(r.data)).catch(() => {});
    API.get("/messages").then((r) => setMessages(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const payload = { ...form, rating: Number(form.rating) || 5 };
      if (editId) await API.put(`/testimonials/${editId}`, payload);
      else await API.post("/testimonials", payload);
      toast.success("Testimoni disimpan");
      setForm(null);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  const removeT = async (id) => {
    if (!window.confirm("Hapus testimoni ini?")) return;
    await API.delete(`/testimonials/${id}`);
    toast.success("Testimoni dihapus");
    load();
  };

  const removeM = async (id) => {
    await API.delete(`/messages/${id}`);
    toast.success("Pesan dihapus");
    load();
  };

  const inputCls = "w-full bg-white border border-slate-300 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C9A227]";
  const labelCls = "text-xs font-bold uppercase tracking-widest text-slate-500 block mb-1.5";

  return (
    <div className="space-y-10" data-testid="admin-content">
      <div>
        <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
          <div>
            <h1 className="font-display text-2xl font-bold text-[#0B1F3A]">Testimoni</h1>
            <p className="text-sm text-slate-500">Ulasan yang tampil di halaman depan</p>
          </div>
          <button onClick={() => { setForm({ ...EMPTY_T }); setEditId(null); }} data-testid="add-testimonial" className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#0B1F3A] bg-navy-hover text-white text-sm font-bold rounded-md transition-colors">
            <Plus className="h-4 w-4" /> Tambah Testimoni
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {testimonials.map((t) => (
            <div key={t.id} data-testid={`testimonial-card-${t.id}`} className="bg-white border border-slate-200 rounded-md p-5">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[#C9A227] text-sm">{"★".repeat(t.rating)}</span>
                {!t.is_visible && <span className="text-[10px] font-bold bg-slate-200 text-slate-500 px-2 py-0.5 rounded-full">TERSEMBUNYI</span>}
              </div>
              <p className="text-sm text-slate-600 line-clamp-3 mb-3">"{t.content}"</p>
              <p className="text-xs font-bold">{t.name} <span className="font-normal text-slate-400">— {t.role}</span></p>
              <div className="flex gap-2 mt-3">
                <button onClick={() => { setForm({ name: t.name, role: t.role || "", content: t.content, rating: t.rating, video_url: t.video_url || "", is_visible: t.is_visible }); setEditId(t.id); }} data-testid={`edit-testimonial-${t.id}`} className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 border border-slate-300 text-xs font-bold rounded-md hover:border-[#0B1F3A]">
                  <Pencil className="h-3.5 w-3.5" /> Edit
                </button>
                <button onClick={() => removeT(t.id)} data-testid={`delete-testimonial-${t.id}`} className="px-3 py-2 border border-red-300 text-red-600 text-xs font-bold rounded-md hover:bg-red-50">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ))}
          {testimonials.length === 0 && <p className="text-slate-400 col-span-full text-sm">Belum ada testimoni.</p>}
        </div>
      </div>

      <div>
        <h2 className="font-display text-2xl font-bold text-[#0B1F3A] mb-1">Pesan Masuk</h2>
        <p className="text-sm text-slate-500 mb-5">Pesan dari formulir kontak halaman depan</p>
        <div className="space-y-3" data-testid="messages-list">
          {messages.map((m) => (
            <div key={m.id} data-testid={`message-${m.id}`} className="bg-white border border-slate-200 rounded-md p-5 flex items-start justify-between gap-4">
              <div>
                <p className="font-bold text-sm">{m.name} <span className="font-normal text-slate-400">• {m.contact}</span></p>
                <p className="text-sm text-slate-600 mt-1">{m.message}</p>
                <p className="text-xs text-slate-400 mt-2">{fmtDate(m.created_at)}</p>
              </div>
              <button onClick={() => removeM(m.id)} data-testid={`delete-message-${m.id}`} className="p-1.5 text-slate-400 hover:text-red-600 shrink-0">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
          {messages.length === 0 && <p className="text-slate-400 text-sm">Belum ada pesan masuk.</p>}
        </div>
      </div>

      {form && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setForm(null)} />
          <div className="relative bg-white rounded-md border border-slate-200 w-full max-w-md p-6" data-testid="testimonial-dialog">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-bold">{editId ? "Edit Testimoni" : "Tambah Testimoni"}</h2>
              <button onClick={() => setForm(null)}><X className="h-5 w-5 text-slate-400" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className={labelCls}>Nama *</label>
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="t-name" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Peran / Asal</label>
                <input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="t-role" className={inputCls} placeholder="Mis: Pemilik Toko, Palembang" />
              </div>
              <div>
                <label className={labelCls}>Isi Testimoni *</label>
                <textarea rows={3} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} data-testid="t-content" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Video Review Positif (URL YouTube / mp4, opsional)</label>
                <input value={form.video_url} onChange={(e) => setForm({ ...form, video_url: e.target.value })} data-testid="t-video" className={inputCls} placeholder="https://youtube.com/watch?v=..." />
                <p className="text-xs text-slate-400 mt-1.5">Video review konsumen/reviewer tampil di bagian Testimoni halaman depan.</p>
              </div>
              <div className="grid grid-cols-2 gap-3 items-end">
                <div>
                  <label className={labelCls}>Rating (1-5)</label>
                  <input type="number" min="1" max="5" value={form.rating} onChange={(e) => setForm({ ...form, rating: e.target.value })} data-testid="t-rating" className={inputCls} />
                </div>
                <label className="flex items-center gap-2 text-sm pb-2.5 cursor-pointer">
                  <input type="checkbox" checked={form.is_visible} onChange={(e) => setForm({ ...form, is_visible: e.target.checked })} data-testid="t-visible" className="h-4 w-4 accent-[#0B1F3A]" />
                  Tampilkan
                </label>
              </div>
              <button onClick={save} disabled={!form.name || !form.content} data-testid="save-testimonial" className="w-full px-6 py-3 bg-gold bg-gold-hover text-[#0B1F3A] font-bold text-sm rounded-md transition-colors disabled:opacity-50">
                Simpan
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
